import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaEnvelope, 
  FaLock, 
  FaUser, 
  FaPhone, 
  FaGoogle, 
  FaFacebookF,
  FaEye,
  FaEyeSlash,
  FaArrowRight,
  FaCheckCircle
} from 'react-icons/fa';
import { MdEmail, MdLock, MdPerson, MdPhone, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import logo from "../assets/Screenshot 2026-02-17 194019.png";

// API Service
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const authService = {
  signUp: async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Sign up failed');
      }
      
      // Store token if returned
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      
      return data;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  },

  signIn: async (credentials) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Sign in failed');
      }
      
      // Store token if returned
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      
      return data;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  },

  signOut: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  }
};

export default function AuthComponent() {
  const navigate = useNavigate();
  const [isSignIn, setIsSignIn] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user types
    setError('');
  };

  const validateForm = () => {
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Password validation (minimum 6 characters)
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    // Sign up specific validations
    if (!isSignIn) {
      // Name validation
      if (!formData.name.trim()) {
        setError('Name is required');
        return false;
      }

      // Phone validation (basic Indian phone number format)
      const phoneRegex = /^[6-9]\d{9}$/;
      if (!phoneRegex.test(formData.phone.replace(/\D/g, ''))) {
        setError('Please enter a valid 10-digit Indian phone number');
        return false;
      }

      // Confirm password
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }

      // Terms agreement
      if (!agreeTerms) {
        setError('Please agree to the Terms & Conditions');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let response;
      
      if (isSignIn) {
        // Sign In
        response = await authService.signIn({
          email: formData.email,
          password: formData.password,
          rememberMe
        });
        
        setSuccess('Login successful! Redirecting...');
        
        // Redirect to dashboard or home after successful login
        setTimeout(() => {
          navigate('/dashboard'); // or '/' for home page
        }, 1500);
        
      } else {
        // Sign Up
        response = await authService.signUp({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password
        });
        
        setSuccess('Account created successfully! Please check your email for verification.');
        
        // Clear form after successful signup
        setFormData({
          name: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: ''
        });
        setAgreeTerms(false);
        
        // Optionally switch to sign in after signup
        setTimeout(() => {
          setIsSignIn(true);
        }, 2000);
      }
      
      console.log('Auth response:', response);
      
    } catch (error) {
      setError(error.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    // Redirect to OAuth provider
    // window.location.href = `${API_BASE_URL}/auth/${provider}`;
  };

  const handleForgotPassword = () => {
    // Navigate to forgot password page or show modal
    navigate('/forgot-password');
  };

  const toggleMode = () => {
    setIsSignIn(!isSignIn);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: ''
    });
    setError('');
    setSuccess('');
    setAgreeTerms(false);
    setRememberMe(false);
  };

  return (
    <div style={styles.container}>
      {/* Background Decoration */}
      <div style={styles.bgDecoration1} />
      <div style={styles.bgDecoration2} />

      {/* Main Card */}
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoContainer}>
          <img src={logo} alt="PlusCabs" style={{ height: 50 }} />
        </div>

        <h2 style={styles.title}>
          {isSignIn ? 'Welcome Back!' : 'Create Account'}
        </h2>
        <p style={styles.subtitle}>
          {isSignIn 
            ? 'Sign in to continue your journey' 
            : 'Join PlusCabs for a seamless ride experience'}
        </p>

        {/* Error/Success Messages */}
        {error && (
          <div style={styles.errorMessage}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={styles.successMessage}>
            <FaCheckCircle style={{ marginRight: 8 }} />
            {success}
          </div>
        )}

        {/* Tab Switcher */}
        <div style={styles.tabContainer}>
          <button
            onClick={() => setIsSignIn(true)}
            style={{
              ...styles.tabButton,
              ...(isSignIn ? styles.tabButtonActive : {})
            }}
          >
            Sign In
          </button>
          <button
            onClick={() => setIsSignIn(false)}
            style={{
              ...styles.tabButton,
              ...(isSignIn ? {} : styles.tabButtonActive)
            }}
          >
            Sign Up
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Name Field - Sign Up Only */}
          {!isSignIn && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <FaUser style={styles.labelIcon} /> Full Name
              </label>
              <div style={styles.inputWrapper}>
                <FaUser style={styles.inputIcon} />
                <input
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  style={styles.input}
                  required={!isSignIn}
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {/* Email Field */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <FaEnvelope style={styles.labelIcon} /> Email Address
            </label>
            <div style={styles.inputWrapper}>
              <FaEnvelope style={styles.inputIcon} />
              <input
                type="email"
                name="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Phone Field - Sign Up Only */}
          {!isSignIn && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <FaPhone style={styles.labelIcon} /> Phone Number
              </label>
              <div style={styles.inputWrapper}>
                <FaPhone style={styles.inputIcon} />
                <input
                  type="tel"
                  name="phone"
                  placeholder="9876543210"
                  value={formData.phone}
                  onChange={handleChange}
                  style={styles.input}
                  required={!isSignIn}
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {/* Password Field */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <FaLock style={styles.labelIcon} /> Password
            </label>
            <div style={styles.inputWrapper}>
              <FaLock style={styles.inputIcon} />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                style={styles.input}
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
                disabled={loading}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Confirm Password - Sign Up Only */}
          {!isSignIn && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <FaLock style={styles.labelIcon} /> Confirm Password
              </label>
              <div style={styles.inputWrapper}>
                <FaLock style={styles.inputIcon} />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  style={styles.input}
                  required={!isSignIn}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.eyeButton}
                  disabled={loading}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
          )}

          {/* Options Row */}
          <div style={styles.optionsRow}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={isSignIn ? rememberMe : agreeTerms}
                onChange={(e) => isSignIn 
                  ? setRememberMe(e.target.checked) 
                  : setAgreeTerms(e.target.checked)
                }
                style={styles.checkbox}
                disabled={loading}
              />
              <span style={styles.checkboxText}>
                {isSignIn ? 'Remember me' : 'I agree to the Terms & Conditions'}
              </span>
            </label>
            
            {isSignIn && (
              <button 
                type="button" 
                style={styles.forgotButton}
                onClick={handleForgotPassword}
                disabled={loading}
              >
                Forgot Password?
              </button>
            )}
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            style={{
              ...styles.submitButton,
              ...(loading ? styles.submitButtonDisabled : {})
            }}
            disabled={loading}
          >
            {loading ? 'Processing...' : (isSignIn ? 'Sign In' : 'Create Account')}
            {!loading && <FaArrowRight style={styles.submitIcon} />}
          </button>

          {/* Social Login */}
          <div style={styles.socialContainer}>
            <p style={styles.socialText}>Or continue with</p>
            <div style={styles.socialButtons}>
              <button 
                type="button" 
                style={styles.socialButton}
                onClick={() => handleSocialLogin('google')}
                disabled={loading}
              >
                <FaGoogle style={styles.socialIcon} />
              </button>
              <button 
                type="button" 
                style={styles.socialButton}
                onClick={() => handleSocialLogin('facebook')}
                disabled={loading}
              >
                <FaFacebookF style={styles.socialIcon} />
              </button>
            </div>
          </div>

          {/* Toggle Mode */}
          <div style={styles.toggleContainer}>
            <p style={styles.toggleText}>
              {isSignIn ? "Don't have an account?" : "Already have an account?"}
            </p>
            <button
              type="button"
              onClick={toggleMode}
              style={styles.toggleButton}
              disabled={loading}
            >
              {isSignIn ? 'Sign Up' : 'Sign In'}
            </button>
          </div>
        </form>

        {/* Features List - Sign Up Only */}
        {!isSignIn && !loading && (
          <div style={styles.featuresContainer}>
            <div style={styles.featureItem}>
              <FaCheckCircle style={styles.featureIcon} />
              <span style={styles.featureText}>Get ₹100 off on first ride</span>
            </div>
            <div style={styles.featureItem}>
              <FaCheckCircle style={styles.featureIcon} />
              <span style={styles.featureText}>Track your rides in real-time</span>
            </div>
            <div style={styles.featureItem}>
              <FaCheckCircle style={styles.featureIcon} />
              <span style={styles.featureText}>24/7 customer support</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Add new styles for messages

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    position: 'relative',
    backgroundColor: '#FFFFFF',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    overflow: 'hidden',
  },
  bgDecoration1: {
    position: 'absolute',
    top: -100,
    right: -100,
    width: 300,
    height: 300,
    borderRadius: '50%',
    backgroundColor: '#FFD700',
    opacity: 0.1,
    zIndex: 0,
  },
  bgDecoration2: {
    position: 'absolute',
    bottom: -100,
    left: -100,
    width: 250,
    height: 250,
    borderRadius: '50%',
    backgroundColor: '#000000',
    opacity: 0.05,
    zIndex: 0,
  },
  card: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#FFFFFF',
    borderRadius: 32,
    padding: '40px 32px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,215,0,0.2)',
    border: '1px solid #FFE680',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 24,
  },
  logoWrapper: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#FFD700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'rotate(-5deg)',
  },
  logoText: {
    fontSize: 24,
    transform: 'rotate(5deg)',
  },
  brandName: {
    fontSize: 28,
    fontWeight: 700,
    color: '#000000',
    fontFamily: "'Poppins', sans-serif",
  },
  brandAccent: {
    color: '#FFD700',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#000000',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: '#666666',
    textAlign: 'center',
    marginBottom: 32,
  },
  tabContainer: {
    display: 'flex',
    gap: 12,
    marginBottom: 32,
    padding: 4,
    backgroundColor: '#F5F5F5',
    borderRadius: 16,
  },
  tabButton: {
    flex: 1,
    padding: '12px',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: 'transparent',
    color: '#666666',
  },
  tabButtonActive: {
    backgroundColor: '#FFD700',
    color: '#000000',
    boxShadow: '0 4px 12px rgba(255,215,0,0.3)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: '#000000',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  labelIcon: {
    color: '#FFD700',
    fontSize: 14,
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputIcon: {
    position: 'absolute',
    left: 16,
    color: '#FFD700',
    fontSize: 16,
  },
  input: {
    width: '100%',
    padding: '14px 16px 14px 48px',
    fontSize: 15,
    border: '1.5px solid #FFE680',
    borderRadius: 16,
    outline: 'none',
    transition: 'all 0.2s ease',
    backgroundColor: '#FFFFFF',
    color: '#000000',
    ':focus': {
      borderColor: '#FFD700',
      boxShadow: '0 0 0 3px rgba(255,215,0,0.1)',
    },
    ':hover': {
      borderColor: '#FFD700',
    },
  },
  eyeButton: {
    position: 'absolute',
    right: 16,
    background: 'none',
    border: 'none',
    color: '#999999',
    cursor: 'pointer',
    fontSize: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      color: '#FFD700',
    },
  },
  optionsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  },
  checkbox: {
    width: 18,
    height: 18,
    cursor: 'pointer',
    accentColor: '#FFD700',
  },
  checkboxText: {
    fontSize: 13,
    color: '#666666',
  },
  forgotButton: {
    background: 'none',
    border: 'none',
    fontSize: 13,
    color: '#FFD700',
    fontWeight: 600,
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  submitButton: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#000000',
    border: 'none',
    borderRadius: 16,
    fontSize: 16,
    fontWeight: 600,
    color: '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    transition: 'all 0.2s ease',
    marginTop: 8,
    ':hover': {
      backgroundColor: '#FFD700',
      color: '#000000',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 20px rgba(255,215,0,0.4)',
    },
  },
  submitIcon: {
    fontSize: 16,
    transition: 'transform 0.2s ease',
    ':hover': {
      transform: 'translateX(4px)',
    },
  },
  socialContainer: {
    marginTop: 24,
    textAlign: 'center',
  },
  socialText: {
    fontSize: 13,
    color: '#999999',
    marginBottom: 16,
    position: 'relative',
    '::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      width: '30%',
      height: 1,
      backgroundColor: '#FFE680',
    },
    '::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: '50%',
      width: '30%',
      height: 1,
      backgroundColor: '#FFE680',
    },
  },
  socialButtons: {
    display: 'flex',
    gap: 16,
    justifyContent: 'center',
  },
  socialButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    border: '1.5px solid #FFE680',
    backgroundColor: '#FFFFFF',
    color: '#000000',
    fontSize: 20,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#FFD700',
      borderColor: '#FFD700',
      transform: 'scale(1.1)',
    },
  },
  socialIcon: {
    fontSize: 20,
  },
  toggleContainer: {
    marginTop: 24,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  toggleText: {
    fontSize: 14,
    color: '#666666',
  },
  toggleButton: {
    background: 'none',
    border: 'none',
    fontSize: 14,
    fontWeight: 700,
    color: '#FFD700',
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  featuresContainer: {
    marginTop: 32,
    padding: '20px 16px',
    backgroundColor: '#FFFDF0',
    borderRadius: 16,
    border: '1px solid #FFE680',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
    ':last-child': {
      marginBottom: 0,
    },
  },
  featureIcon: {
    color: '#2E7D32',
    fontSize: 18,
  },
  featureText: {
    fontSize: 14,
    color: '#000000',
  },
   errorMessage: {
    backgroundColor: '#FFEBEE',
    color: '#C62828',
    padding: '12px 16px',
    borderRadius: 12,
    fontSize: 14,
    marginBottom: 20,
    border: '1px solid #FFCDD2',
  },
  successMessage: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    padding: '12px 16px',
    borderRadius: 12,
    fontSize: 14,
    marginBottom: 20,
    border: '1px solid #A5D6A7',
    display: 'flex',
    alignItems: 'center',
  },
  submitButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    ':hover': {
      transform: 'none',
      boxShadow: 'none',
    },}
};

// Add global styles
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@700&display=swap');
  
  body {
    margin: 0;
    padding: 0;
    background-color: #FFFFFF;
  }
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  button {
    font-family: inherit;
  }
`;

// Inject global styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = globalStyles;
  document.head.appendChild(style);
}

// Add logo container style
styles.logoContainer = {
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 24,
};

// Export the auth service for use in other components
export { authService };